import React, { useState, useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Button,
  useMediaQuery,
  useTheme,
  Chip,
  IconButton,
  CircularProgress,
  Pagination,
} from "@mui/material"
import { useFavoritesListQuery, useToggleFavoriteMutation, useSearchSalonsV2Query } from "../../../services/salonsApi"
import { usePublicAllCategoriesQuery } from "../../../services/categoriesApi"
import { useAuth } from "../../../context/AuthContext"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import "maplibre-gl/dist/maplibre-gl.css"
import "@maplibre/maplibre-gl-leaflet"
// Icons
import LocationOnIcon from "@mui/icons-material/LocationOn"
import StarIcon from '@mui/icons-material/Star';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import MapIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/List';
// Custom Components
import theme from "../../../theme"
import { useTranslation } from 'react-i18next'
import defaultSalonImage from "../../../assets/salon2.jpeg"
import UnifiedSalonSearchForm from "./UnifiedSalonSearchForm";
import {
  buildSalonSearchRequest,
  buildSearchSalonUrl,
  readSalonSearchFromUrl,
} from "./searchSalonQuery";

const RESULTS_PER_PAGE = 6;
const SEARCH_SALON_STATE_KEY = "glow.searchSalonState";
const DEFAULT_SALON_IMAGE = defaultSalonImage;

const readStoredSearchState = () => {
  if (typeof window === "undefined") return null;

  try {
    const rawState = window.sessionStorage.getItem(SEARCH_SALON_STATE_KEY);
    if (!rawState) return null;
    const parsedState = JSON.parse(rawState);
    return parsedState && typeof parsedState === "object" ? parsedState : null;
  } catch (error) {
    return null;
  }
};

const readSearchStateFromUrl = () => {
  if (typeof window === "undefined") return null;

  const parsed = readSalonSearchFromUrl(window.location.search);
  if (!parsed.hasActiveSearch) return null;
  const requestParams = buildSalonSearchRequest({
    ...parsed.formValue,
    page: parsed.page,
    pageSize: RESULTS_PER_PAGE,
  });

  return {
    formValue: parsed.formValue,
    requestParams,
    currentPage: parsed.page,
    resultsScrollTop: 0,
    returnUrl: buildSearchSalonUrl({
      ...parsed.formValue,
      page: parsed.page,
      active: true,
    }),
  };
};

// Fix for default markers in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png"
})

// Custom salon marker icon (optimized for better visibility)
const salonIcon = L.divIcon({
  className: "custom-salon-marker",
  html: `<div class="salon-marker-inner" style="
    background-color: ${theme.colors.primary[600]};
    color: white;
    border-radius: 50% 50% 50% 0;
    width: 32px;
    height: 32px;
    transform: rotate(-45deg);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    border: 2px solid white;
    cursor: pointer;
    transition: all 0.2s ease;
  ">
    <span style="
      transform: rotate(45deg);
      font-size: 16px;
    ">💇</span>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [12, 30],
  popupAnchor: [0, -30],
})
// Add custom CSS for salon tooltips and popups
const customMapStyles = `
  .salon-tooltip {
    background-color: ${theme.colors.primary[700]} !important;
    border: 2px solid white !important;
    border-radius: 8px !important;
    color: white !important;
    font-weight: bold !important;
    padding: 8px 12px !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
    font-size: 14px !important;
  }
  .salon-tooltip::before {
    border-top-color: ${theme.colors.primary[700]} !important;
  }
  .leaflet-popup-content-wrapper {
    border-radius: 12px !important;
    box-shadow: 0 6px 20px rgba(0,0,0,0.15) !important;
  }
  .custom-salon-marker {
    background: transparent !important;
  }
  .salon-marker-inner:hover {
    transform: rotate(-45deg) scale(1.15) !important;
    box-shadow: 0 5px 12px rgba(0,0,0,0.4) !important;
  }
  
  /* Custom zoom controls styling */
  .leaflet-control-zoom {
    border: none !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
  }
  .leaflet-control-zoom a {
    width: 40px !important;
    height: 40px !important;
    line-height: 40px !important;
    font-size: 20px !important;
    border: none !important;
    background-color: white !important;
    color: #333 !important;
    transition: all 0.2s ease !important;
  }
  .leaflet-control-zoom a:hover {
    background-color: #f5f5f5 !important;
    color: ${theme.colors.primary[600]} !important;
  }
  .leaflet-control-zoom a:first-child {
    border-radius: 8px 8px 0 0 !important;
  }
  .leaflet-control-zoom a:last-child {
    border-radius: 0 0 8px 8px !important;
  }
  .leaflet-bar {
    border: none !important;
  }

  @media (max-width: 768px) {
    .leaflet-popup-content-wrapper {
      max-width: 280px !important;
      margin: 0 !important;
    }
    .leaflet-popup-content {
      margin: 8px 12px !important;
      font-size: 14px !important;
    }
    .salon-tooltip {
      font-size: 12px !important;
      padding: 6px 8px !important;
    }
    .leaflet-control-zoom a {
      width: 36px !important;
      height: 36px !important;
      line-height: 36px !important;
      font-size: 18px !important;
    }
  }
`



const SearchSalonPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"))
  const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down("sm"))
  const [showMapOnMobile, setShowMapOnMobile] = useState(false)
  const restoredSearchStateRef = useRef((() => {
    const urlState = readSearchStateFromUrl();
    if (!urlState) return null;

    const storedState = readStoredSearchState();
    if (storedState?.returnUrl === urlState.returnUrl) {
      return {
        ...urlState,
        resultsScrollTop: storedState.resultsScrollTop || 0,
      };
    }

    return urlState;
  })());
  const restoredSearchState = restoredSearchStateRef.current || {};

  // Helper function to get translated field
  const getTranslatedField = (item, field) => {
    const translationsKey = `${field}_translations`;
    const localizedKey = `localized_${field}`;
    const translations = item?.[translationsKey] || {};
    
    // Check if we have a translation for the current language
    const currentLanguage = (i18n.language || 'fr').split('-')[0].toLowerCase();
    if (translations[currentLanguage]) {
      return translations[currentLanguage];
    }
    
    // Fallback to localized field if available
    if (item?.[localizedKey]) {
      return item[localizedKey];
    }
    
    // Final fallback to original field
    return item?.[field] || '';
  };
  let salonsToShow = [];
  let showNoResultsMessage = false;
  const [searchFormValue, setSearchFormValue] = useState(() => restoredSearchState.formValue || {
    query: "",
    categoryId: "",
    radiusKm: "all",
    selectedPlace: null,
    coordinates: null,
    mode: "text",
  })
  const [requestParams, setRequestParams] = useState(() => restoredSearchState.requestParams || null)
  const [currentPage, setCurrentPage] = useState(() => restoredSearchState.currentPage || 1)
  const salonNameSearch = searchFormValue.query || "";
  const hasActiveSearch = Boolean(requestParams);
  const activeSearchHeaderHeight = isMobile ? "auto" : 252;
  const activeSearchPanelHeight = isMobile ? "auto" : `calc(100vh - ${activeSearchHeaderHeight}px)`;

  // Authentication
  const { isAuthenticated } = useAuth();

  // Favorites mutations and queries - only when authenticated
  const [toggleFavorite] = useToggleFavoriteMutation();
  const { data: userFavorites, refetch: refetchFavorites } = useFavoritesListQuery(
    undefined,
    { skip: !isAuthenticated() }
  );
  // --- State Management ---
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const salonMarkersRef = useRef([])
  const resultsScrollRef = useRef(null)
  const restoredScrollTopRef = useRef(Number(restoredSearchState.resultsScrollTop || 0))
  const searchMemoryRef = useRef({
    formValue: searchFormValue,
    requestParams,
    currentPage,
    resultsScrollTop: Number(restoredSearchState.resultsScrollTop || 0),
    returnUrl: restoredSearchState.returnUrl || buildSearchSalonUrl({
      ...searchFormValue,
      page: currentPage,
      active: Boolean(requestParams),
    }),
  })

  const persistSearchState = (overrides = {}) => {
    if (typeof window === "undefined") return;

    const nextState = {
      ...searchMemoryRef.current,
      resultsScrollTop: resultsScrollRef.current
        ? resultsScrollRef.current.scrollTop
        : searchMemoryRef.current.resultsScrollTop,
      ...overrides,
    };

    searchMemoryRef.current = nextState;
    window.sessionStorage.setItem(SEARCH_SALON_STATE_KEY, JSON.stringify(nextState));
  };

  const handleResultsScroll = (event) => {
    searchMemoryRef.current = {
      ...searchMemoryRef.current,
      resultsScrollTop: event.currentTarget.scrollTop,
    };
  };

  const goToSalonDetails = (salon) => {
    const salonId = salon?.salon_id || salon?.id;
    if (!salonId) return;

    persistSearchState();
    navigate(`/salon/${salonId}`, {
      state: {
        fromSearchSalon: true,
        from: searchMemoryRef.current.returnUrl,
      },
    });
  };

  const titleStyles = {
    fontSize: isMobile ? (isSmallMobile ? "2rem" : "2.5rem") : "3.5rem",
    fontWeight: 700,
    color: theme.colors.primary[800],
    marginBottom: isMobile ? "20px" : "32px",
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
    maxWidth: isMobile ? "100%" : "900px",
  }

  const subtitleStyles = {
    fontSize: isMobile ? (isSmallMobile ? "1rem" : "1.1rem") : "1.25rem",
    color: theme.colors.text.secondary,
    marginBottom: isMobile ? "40px" : "64px",
    lineHeight: 1.6,
    maxWidth: isMobile ? "100%" : "700px",
    fontWeight: 400,
    opacity: 0.9,
  }
  // --- Data Fetching ---
  // Fetch categories from the new categoriesApi
  const { data: categories, isLoading: categoriesLoading } = usePublicAllCategoriesQuery();
  const unifiedCategories = (categories || []).map((category) => ({
    id: category?.category_id != null ? String(category.category_id) : "",
    label: getTranslatedField(category, "name"),
  }));
  const { data: searchResults, isLoading: searchLoading, isFetching: searchFetching } = useSearchSalonsV2Query(
    requestParams || {},
    {
      skip: !requestParams,
      refetchOnMountOrArgChange: true
    }
  )

  useEffect(() => {
    searchMemoryRef.current = {
      ...searchMemoryRef.current,
      formValue: searchFormValue,
      requestParams,
      currentPage,
      returnUrl: buildSearchSalonUrl({
        ...searchFormValue,
        page: currentPage,
        active: Boolean(requestParams),
      }),
    };
  }, [searchFormValue, requestParams, currentPage]);

  useEffect(() => {
    const urlState = readSearchStateFromUrl();

    if (!urlState) {
      setSearchFormValue({
        query: "",
        categoryId: "",
        radiusKm: "all",
        selectedPlace: null,
        coordinates: null,
        mode: "text",
      });
      setRequestParams(null);
      setCurrentPage(1);
      restoredScrollTopRef.current = 0;
      return;
    }

    setSearchFormValue(urlState.formValue);
    setRequestParams(urlState.requestParams);
    setCurrentPage(urlState.currentPage);
  }, [location.search]);

  // Effet pour gérer le redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update Leaflet zoom control titles when language changes
  useEffect(() => {
    const map = mapInstanceRef.current
    if (!map) return

    const container = map.getContainer?.()
    if (!container) return

    const zoomInEl = container.querySelector?.('.leaflet-control-zoom-in')
    const zoomOutEl = container.querySelector?.('.leaflet-control-zoom-out')
    if (zoomInEl) zoomInEl.setAttribute('title', t('landing_search_salon.zoom_in'))
    if (zoomOutEl) zoomOutEl.setAttribute('title', t('landing_search_salon.zoom_out'))
  }, [i18n.language, t]);

  // --- Event Handlers & Effects ---
  useEffect(() => {
    console.log("🗺️ Map useEffect triggered", {
      hasMapRef: !!mapRef.current,
      hasMapInstance: !!mapInstanceRef.current
    });

    if (!mapRef.current || mapInstanceRef.current) {
      console.log("⚠️ Map initialization skipped:", {
        noMapRef: !mapRef.current,
        alreadyInitialized: !!mapInstanceRef.current
      });
      return;
    }

    const initMapTimeout = setTimeout(() => {
      console.log("🚀 Starting map initialization...");
      try {
        // Add custom CSS for map tooltips
        if (!document.getElementById("map-custom-styles")) {
          const styleEl = document.createElement("style")
          styleEl.id = "map-custom-styles"
          styleEl.innerHTML = customMapStyles
          document.head.appendChild(styleEl)
        }

        // Create map centered on user's location or default to Algiers
        const defaultCoords = [36.7538, 3.0588] // Algiers coordinates as fallback
        console.log("📍 Creating Leaflet map at coords:", defaultCoords);
        const map = L.map(mapRef.current, {
          zoomControl: false, // We'll add custom zoom control
          attributionControl: true,
          zoomAnimation: true,
          fadeAnimation: true,
          markerZoomAnimation: true,
          zoomSnap: 0.5,
          zoomDelta: 0.5,
          wheelPxPerZoomLevel: 120,
        }).setView(defaultCoords, 13) // Default zoom level 13 for better POI visibility

        // Add custom zoom control in bottom-right
        L.control.zoom({
          position: 'bottomright',
          zoomInTitle: t('landing_search_salon.zoom_in'),
          zoomOutTitle: t('landing_search_salon.zoom_out')
        }).addTo(map);

        // Try to get user's current location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords
              map.setView([latitude, longitude], 14, {
                animate: true,
                duration: 1
              })
            },
            (error) => {
              console.warn('Could not get user location:', error)
              // Keep default Algiers view
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
          )
        }

        // Base tiles: OSM as reliable fallback so the map always renders
        const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map)

        // Try MapLibre GL vector tiles (MapTiler). If it fails, OSM remains visible.
        try {
          if (L.maplibreGL) {
            console.log("🎨 Adding MapLibre GL layer...");
            L.maplibreGL({
              style: "https://api.maptiler.com/maps/streets-v2/style.json?key=get_your_own_OpIi9ZULNHzrESv6T2vL",
              attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>',
              interactive: false  // Let Leaflet handle interactions
            }).addTo(map)
          }
        } catch (e) {
          console.warn("MapLibre GL layer failed, using OSM fallback only:", e)
        }

        mapInstanceRef.current = map
        console.log("✅ Map successfully initialized and stored in ref");

        // Global function to handle salon details navigation
        window.openSalonDetails = (salon) => {
          console.log("🔍 openSalonDetails called with:", salon);
          console.log("📊 Salon data type:", typeof salon);
          console.log("🆔 Salon ID:", salon.salon_id || salon.id);
          goToSalonDetails(salon)
        }

        setTimeout(() => {
          if (map) {
            console.log("📐 Invalidating map size...");
            map.invalidateSize()
          }
        }, 100)
      } catch (error) {
        console.error("Map initialization error:", error)
      }
    }, 500)

    return () => {
      clearTimeout(initMapTimeout)
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
        salonMarkersRef.current = []
      }
    }
  }, [])

  // Handle window resize for map
  useEffect(() => {
    const handleResize = () => {
      if (mapInstanceRef.current) {
        setTimeout(() => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.invalidateSize()
          }
        }, 100)
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // When layout toggles (search shown, mobile map shown), reflow the map
  useEffect(() => {
    if (!mapInstanceRef.current) return
    // Only run when the section is actually shown
    if (!hasActiveSearch) return
    setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize()
      }
    }, 150)
  }, [hasActiveSearch, showMapOnMobile, isMobile])

  // Update map with salon markers
  useEffect(() => {
    // console.log("Map markers update triggered", {
    //   hasMap: !!mapInstanceRef.current,
    //   salonsCount: salonsToShow.length,
    //   salonsToShow: salonsToShow.map(s => ({ 
    //     name: s.name, 
    //     lat: s.latitude, 
    //     lng: s.longitude 
    //   }))
    // });

    if (!mapInstanceRef.current) return;

    // Nettoyer les marqueurs existants
    salonMarkersRef.current.forEach(marker => {
      mapInstanceRef.current.removeLayer(marker);
    });
    salonMarkersRef.current = [];

    // Ajouter les nouveaux marqueurs avec des popups détaillés
    console.log(" Adding markers for salons:", salonsToShow.length);
    salonsToShow.forEach(salon => {
      console.log("?? Processing salon:", getTranslatedField(salon, 'name'), "ID:", salon.salon_id || salon.id);
      if (salon.latitude && salon.longitude) {
        const lat = Number.parseFloat(salon.latitude);
        const lng = Number.parseFloat(salon.longitude);
        console.log(" Coordinates:", { lat, lng });

        if (!isNaN(lat) && !isNaN(lng)) {
          // Préparer le contenu du popup
          const BASE_IMG = "https://glow.dev.digitalia-ltd.com";
          const firstPhotoUrl = salon.photos?.[0]?.url || "";
          const photoSrc = firstPhotoUrl
            ? firstPhotoUrl.startsWith("http")
              ? firstPhotoUrl
              : `${BASE_IMG}${firstPhotoUrl.startsWith("/") ? "" : "/"}${firstPhotoUrl}`
            : "";

          const popupContent = `
            <div style="max-width: 280px; font-family: 'Roboto', sans-serif;">
              ${photoSrc ? `
                <img src="${photoSrc}"
                     alt="${getTranslatedField(salon, 'name')}"
                     style="width: 100%; height: 160px; object-fit: cover; border-radius: 8px; margin-bottom: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"
                     onerror="this.style.display='none'">
              ` : ''}
              <div style="padding: 8px 0;">
                <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: bold; color: ${theme.colors.primary[800]}; line-height: 1.3;">
                  ${getTranslatedField(salon, 'name')}
                </h3>
                ${salon.distance_km ? `
                  <div style="margin: 8px 0; display: flex; align-items: center; gap: 4px;">
                    <span style="background-color: ${theme.colors.secondary[100]}; color: ${theme.colors.primary[700]}; padding: 4px 8px; border-radius: 12px; font-size: 12px; font-weight: 600;">
                      📍 ${salon.distance_km.toFixed(1)} km
                    </span>
                  </div>
                ` : ''}
                <p style="margin: 8px 0; color: #666; font-size: 14px; line-height: 1.4;">
                  ${salon.formatted_address || salon.address || ''}
                </p>
                ${salon.phone ? `
                  <p style="margin: 8px 0; color: #666; font-size: 14px;">
                    📞 ${salon.phone}
                  </p>
                ` : ''}
                <div style="margin-top: 12px; text-align: center;">
                  <button data-salon-id="${salon.salon_id || salon.id}" data-salon-name="${getTranslatedField(salon, 'name').replace(/"/g, '&quot;')}" onclick="console.log('?? Button clicked for salon:', this.getAttribute('data-salon-name'), 'ID:', this.getAttribute('data-salon-id')); console.log('? window.openSalonDetails exists:', !!window.openSalonDetails); if(window.openSalonDetails) { window.openSalonDetails({salon_id: this.getAttribute('data-salon-id'), id: this.getAttribute('data-salon-id'), name: this.getAttribute('data-salon-name')}) }"
                     style="background-color: ${theme.colors.primary[600]}; color: white; padding: 8px 16px; border: none; border-radius: 20px; cursor: pointer;"
                     onmouseover="this.style.backgroundColor='${theme.colors.primary[700]}'; this.style.transform='translateY(-1px)'"
                     onmouseout="this.style.backgroundColor='${theme.colors.primary[600]}'; this.style.transform='translateY(0)'">
                    ${t('landing_search_salon.view_salon')} →
                  </button>
                </div>
              </div>
            </div>
          `;

          // Créer le marqueur avec le popup
          const marker = L.marker([lat, lng], {
            icon: salonIcon,
            title: getTranslatedField(salon, 'name'),
            alt: `Salon: ${getTranslatedField(salon, 'name')}`,
          })
            .addTo(mapInstanceRef.current)
            .bindPopup(popupContent, {
              maxWidth: 300,
              className: "custom-salon-popup",
            })
            .bindTooltip(
              `<strong>${getTranslatedField(salon, 'name')}</strong><br/>${salon.distance_km ? salon.distance_km.toFixed(1) + " km" : ""}`,
              {
                permanent: false,
                direction: "top",
                className: "salon-tooltip",
                offset: [0, -10],
              }
            );

          salonMarkersRef.current.push(marker);
        }
      }
    });

    // Ajuster la vue pour montrer tous les marqueurs
    if (salonMarkersRef.current.length > 0) {
      const group = new L.featureGroup(salonMarkersRef.current);
      mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1));

      // Si un seul marqueur, zoomer dessus
      if (salonMarkersRef.current.length === 1) {
        const salon = salonsToShow[0];
        if (salon.latitude && salon.longitude) {
          const lat = Number.parseFloat(salon.latitude);
          const lng = Number.parseFloat(salon.longitude);
          if (!isNaN(lat) && !isNaN(lng)) {
            setTimeout(() => {
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setView([lat, lng], 17);
              }
            }, 100);
          }
        }
      }
    }

    // Nettoyage
    return () => {
      if (mapInstanceRef.current) {
        salonMarkersRef.current.forEach(marker => {
          mapInstanceRef.current.removeLayer(marker);
        });
      }
    };
  }, [salonsToShow]); // Se déclenche quand salonsToShow change

  console.log("🔄 Component render - salonsToShow:", salonsToShow.length, "items");




  const handleUnifiedSearch = (nextValue) => {
    const normalizedValue = {
      query: nextValue.query || "",
      categoryId: nextValue.categoryId || "",
      radiusKm: nextValue.radiusKm ?? "all",
      selectedPlace: nextValue.selectedPlace || null,
      coordinates: nextValue.coordinates || null,
      mode: nextValue.mode || "text",
    };
    const apiParams = buildSalonSearchRequest({
      ...normalizedValue,
      page: 1,
      pageSize: RESULTS_PER_PAGE,
    });
    setCurrentPage(1);
    setSearchFormValue(normalizedValue);
    setRequestParams(apiParams);
    navigate(buildSearchSalonUrl({
      ...normalizedValue,
      page: 1,
      active: true,
    }), { replace: true });
  };

  // --- Result Logic ---
  const [lastSearchTerm, setLastSearchTerm] = useState('');

  // Vérifier si on a une recherche active

  const isLoading = searchLoading || searchFetching;

  // Mettre à jour le dernier terme de recherche quand on lance une nouvelle recherche
  // Gestion de l'état de recherche
  useEffect(() => {
    if (requestParams) {
      setLastSearchTerm(requestParams.q ? `"${requestParams.q}"` : '');
    }
  }, [requestParams]);

  useEffect(() => {
    if (mapRef.current) {
      // console.log("Map container dimensions:", {
      //   width: mapRef.current.offsetWidth,
      //   height: mapRef.current.offsetHeight
      // });
    }
  }, [mapRef.current, salonsToShow]);
  // Mise à jour de l'état quand les résultats arrivent et mise à jour des marqueurs
  useEffect(() => {
    // Mettre à jour les marqueurs sur la carte
    if (mapInstanceRef.current && searchResults) {
      const currentSalons = Array.isArray(searchResults)
        ? searchResults
        : (Array.isArray(searchResults?.results) ? searchResults.results : []);

      // Supprimer les anciens marqueurs
      salonMarkersRef.current.forEach(marker => {
        mapInstanceRef.current.removeLayer(marker);
      });
      salonMarkersRef.current = [];

      // Ajouter les nouveaux marqueurs
      currentSalons.forEach(salon => {
        if (salon.latitude && salon.longitude) {
          const lat = Number.parseFloat(salon.latitude);
          const lng = Number.parseFloat(salon.longitude);

          if (!isNaN(lat) && !isNaN(lng)) {
            const marker = L.marker([lat, lng], {
              icon: salonIcon,
              title: getTranslatedField(salon, 'name')
            }).addTo(mapInstanceRef.current);

            // Ajouter un popup avec les informations du salon
            // Format address like in the card
            const addrRaw = salon.formatted_address ?? salon.formattedAddress ?? salon.address ?? '';
            const addressText = addrRaw
              .split(',')
              .map(s => s.trim())
              .filter(Boolean)
              .slice(0, 6)
              .join(', ');

            const popupContent = `
              <div style="min-width: 200px;">
                <h3 style="margin: 0 0 8px 0; font-size: 16px; color: #333;">${getTranslatedField(salon, 'name')}</h3>
                ${addressText ? `
                  <div style="display: flex; align-items: flex-start; gap: 8px; margin: 0 0 8px 0;">
                    <svg style="width: 18px; height: 18px; margin-top: 2px; fill: #666;" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <p style="margin: 0; font-size: 14px; color: #666; line-height: 1.4;">${addressText}</p>
                  </div>
                ` : ''}
                <button 
                  data-salon-id="${salon.salon_id || salon.id}" data-salon-name="${getTranslatedField(salon, 'name').replace(/"/g, '&quot;')}" onclick="console.log('?? Voir détails clicked'); var salonId = this.getAttribute('data-salon-id'); console.log('ID:', salonId); if(window.openSalonDetails) { window.openSalonDetails({salon_id: salonId, id: salonId}) }"
                  style="
                    background-color: #00875A;
                    color: white;
                    border: none;
                    padding: 6px 12px;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                  "
                >
                  Voir les détails
                </button>
              </div>
            `;

            marker.bindPopup(popupContent);
            salonMarkersRef.current.push(marker);
          }
        }
      });

      // Ajuster la vue pour montrer tous les marqueurs
      if (salonMarkersRef.current.length > 0) {
        const group = new L.featureGroup(salonMarkersRef.current);
        mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1));
      } else if (currentSalons.length > 0) {
        // Si pas de coordonnées mais des résultats, centrer sur Paris
        mapInstanceRef.current.setView([48.8566, 2.3522], 12);
      }
    }
  }, [searchResults]);

  // Gérer les résultats de recherche par adresse/service
  if (searchResults) {
    if (Array.isArray(searchResults.results)) {
      salonsToShow = searchResults.results;
    } else if (Array.isArray(searchResults)) {
      salonsToShow = searchResults;
    } else if (searchResults.error) {
      console.error("Erreur de recherche:", searchResults.error);
      showNoResultsMessage = true;
    }
  }

  const totalResults = searchResults?.count ?? salonsToShow.length;
  const pageCount = Math.max(1, searchResults?.total_pages || 1);
  const paginatedSalons = salonsToShow;

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  useEffect(() => {
    if (!hasActiveSearch || !searchResults || !restoredScrollTopRef.current) return;

    const timer = setTimeout(() => {
      if (resultsScrollRef.current) {
        resultsScrollRef.current.scrollTop = restoredScrollTopRef.current;
      }
      restoredScrollTopRef.current = 0;
    }, 0);

    return () => clearTimeout(timer);
  }, [hasActiveSearch, searchResults, currentPage]);

  // Afficher le message "Aucun résultat" si nécessaire
  if (hasActiveSearch && !isLoading && salonsToShow.length === 0) {
    showNoResultsMessage = true;
  }

  // Check if salon is in favorites
  const isSalonFavorited = (salonId) => {
    return userFavorites?.some(favorite => {
      const salon = favorite.salon || favorite;
      return salon.salon_id === salonId;
    }) || false;
  };

  // Get favorite ID for a salon
  const getFavoriteId = (salonId) => {
    const favorite = userFavorites?.find(favorite => {
      const salon = favorite.salon || favorite;
      return salon.salon_id === salonId;
    });
    return favorite?.id;
  };

  // Handle toggling favorite status
  const handleToggleFavorite = async (salon, event) => {
    event.stopPropagation(); // Prevent card click

    try {
      await toggleFavorite(salon.salon_id).unwrap();
      await refetchFavorites(); // Refetch favorites list
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // --- Sub-Components ---
  const SalonCard = ({ salon }) => {
    const BASE_IMG = "https://glow.dev.digitalia-ltd.com"

    // Get today's date for availability check
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    // TODO: Endpoint not generated by RTK Query codegen - needs manual implementation
    const availabilityData = null;
    const availabilityLoading = false;
    const availabilityError = null;
    // const { data: availabilityData, isLoading: availabilityLoading, error: availabilityError } = useSalonsSalonAvailableSlotsRetrieveQuery({
    //   salonId: salon.salon_id,
    //   date: today
    // });



    // Get primary photo or first available photo
    const primaryPhoto = salon.photos?.find(photo => photo.is_primary) || salon.photos?.[0]
    const getPhotoUrl = (url) => {
      if (!url) return DEFAULT_SALON_IMAGE;
      // If URL is already absolute, return as is
      if (url.startsWith('http')) return url;
      // Handle media URLs from Django
      if (url.startsWith('/media/')) {
        return `${window.location.origin}${url}`;
      }
      // Fallback for other relative URLs
      return `${window.location.origin}/media/${url}`;
    };
    const photoSrc = getPhotoUrl(primaryPhoto?.url || primaryPhoto?.image || primaryPhoto?.photo);

    // Format opening hours
    const formatOpeningHours = () => {
      if (!salon.opening_hours || typeof salon.opening_hours !== 'object') {
        return t('landing_search_salon.opening_hours_unavailable');
      }

      const today = new Date().toLocaleString(i18n.language || 'fr-FR', { weekday: 'long' }).toLowerCase();
      const todayHours = salon.opening_hours[today];

      if (!todayHours || todayHours.ouverture === 'fermé' || todayHours.fermeture === 'fermé') {
        return t('landing_search_salon.closed_today');
      }

      // Format time for display (e.g., '09:00' -> '9h00')
      const formatTime = (time) => {
        if (!time || time === 'fermé') return '';
        const [hours, minutes] = time.split(':');
        // Remove leading zero from hours
        const formattedHours = hours.startsWith('0') ? hours.substring(1) : hours;
        return `${formattedHours}h${minutes}`;
      };

      const openingTime = formatTime(todayHours.ouverture);
      const closingTime = formatTime(todayHours.fermeture);

      if (openingTime && closingTime) {
        return t('landing_search_salon.open_today', { opening: openingTime, closing: closingTime });
      }

      return t('landing_search_salon.opening_hours_unavailable');
    };

    // Helper function to get all unique slots
    const getAllUniqueSlots = () => {
      if (availabilityLoading || !availabilityData) return new Set();

      const uniqueSlots = new Set();
      availabilityData.services?.forEach(service => {
        service.team_members?.forEach(member => {
          member.available_slots?.forEach(slot => {
            uniqueSlots.add(slot);
          });
        });
      });
      return uniqueSlots;
    };

    // Get available slots for display (only current and future slots)
    const getAvailableSlots = () => {
      const uniqueSlots = getAllUniqueSlots();
      const now = new Date();
      const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');

      // Filter out past slots and return first 4 future/current slots
      return Array.from(uniqueSlots)
        .filter(slot => slot >= currentTime)
        .sort()
        .slice(0, 4);
    };

    const availableSlots = getAvailableSlots();
    const totalUniqueSlots = getAllUniqueSlots().size;
    const [showAllSlots, setShowAllSlots] = React.useState(false);

    // Get all slots for expanded view (only current and future slots)
    const getAllSlots = () => {
      const uniqueSlots = getAllUniqueSlots();
      const now = new Date();
      const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');

      // Filter out past slots and return all future/current slots
      return Array.from(uniqueSlots)
        .filter(slot => slot >= currentTime)
        .sort();
    };

    // Group today's slots in morning/afternoon
    const now2 = new Date();
    const currentTime2 = now2.getHours().toString().padStart(2, '0') + ':' + now2.getMinutes().toString().padStart(2, '0');
    const allFutureSlots = Array.from(getAllUniqueSlots()).filter(s => s >= currentTime2).sort();
    const morningSlots = allFutureSlots.filter(s => s < '12:00');
    const afternoonSlots = allFutureSlots.filter(s => s >= '12:00');

    return (
      <Card
        onClick={() => goToSalonDetails(salon)}
        sx={{
          mb: isMobile ? 2 : 3,
          borderRadius: isMobile ? '12px' : '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.06)',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
            transform: 'translateY(-2px)'
          }
        }}>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'stretch', gap: isMobile ? 0 : 2 }}>
          {/* Left: image */}
          <Box sx={{ position: 'relative', width: isMobile ? '100%' : 320, minWidth: isMobile ? '100%' : 320, alignSelf: 'stretch', minHeight: isMobile ? 180 : 'auto' }}>
            <CardMedia
              component="img"
              image={photoSrc}
              alt={getTranslatedField(salon, 'name')}
              onError={(event) => {
                if (event.currentTarget.src !== DEFAULT_SALON_IMAGE) {
                  event.currentTarget.src = DEFAULT_SALON_IMAGE;
                }
              }}
              sx={{ position: 'absolute', inset: 0, height: '100%', width: '100%', objectFit: 'cover', display: 'block' }}
            />
            {isAuthenticated() && (
              <IconButton
                onClick={(e) => handleToggleFavorite(salon, e)}
                sx={{
                  position: 'absolute', top: 12, right: 12,
                  backgroundColor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)'
                }}
              >
                {isSalonFavorited(salon.salon_id) ? (
                  <FavoriteIcon sx={{ color: '#e91e63' }} />
                ) : (
                  <FavoriteBorderIcon sx={{ color: theme.colors.primary[600] }} />
                )}
              </IconButton>
            )}
            {salon.distance_km && (
              <Chip label={`${salon.distance_km.toFixed(1)} km`} size="small" sx={{ position: 'absolute', bottom: 12, right: 12, backgroundColor: 'rgba(0,0,0,0.7)', color: 'white' }} />
            )}
          </Box>

          {/* Right: details */}
          <CardContent sx={{ flex: 1, p: isMobile ? 2 : 3, display: 'flex', flexDirection: 'column', gap: isMobile ? 1.25 : 1.5 }}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '1.125rem' }}>
              {getTranslatedField(salon, 'name')}
            </Typography>

            {(() => {
              const addrRaw = salon.formatted_address ?? salon.formattedAddress ?? salon.address ?? '';
              const addressText = addrRaw
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .slice(0, 6)
                .join(', ');
              return addressText ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationOnIcon sx={{ fontSize: '1.1rem', color: 'text.secondary', mt: '2px' }} />
                  <Typography variant="body2" color="text.secondary">{addressText}</Typography>
                </Box>
              ) : null;
            })()}

            {(salon.phone_number || salon.phone) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CallOutlinedIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
                <Button
                  variant="text"
                  size="small"
                  href={`tel:${salon.phone_number || salon.phone}`}
                  sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                >
                  {salon.phone_number || salon.phone}
                </Button>
              </Box>
            )}

            {salon.average_rating !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StarIcon sx={{ color: '#FFB400', fontSize: '1.1rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{salon.average_rating.toFixed(1)}</Typography>
                {salon.review_count > 0 && (
                  <Typography variant="body2" color="text.secondary">({t('landing_search_salon.reviews_count', { count: salon.review_count })})</Typography>
                )}
              </Box>
            )}

            {/* Opening hours (today) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">{formatOpeningHours() || t('landing_search_salon.opening_hours_unavailable')}</Typography>
            </Box>

            {/* Availability today grouped */}
            {(morningSlots.length > 0 || afternoonSlots.length > 0) && (
              <Box sx={{ mt: 0.5 }}>
                {morningSlots.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, minWidth: 56 }}>{t('landing_search_salon.morning')}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(morningSlots.slice(0, 4)).map((slot, i) => (
                        <Chip key={`m-${i}`} label={slot} size="small" variant="outlined" color="primary" clickable onClick={(e) => { e.stopPropagation(); goToSalonDetails(salon); }} />
                      ))}
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, minWidth: 56 }}>{t('landing_search_salon.afternoon')}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(afternoonSlots.slice(0, 4)).map((slot, i) => (
                      <Chip key={`a-${i}`} label={slot} size="small" variant="outlined" color="primary" clickable onClick={(e) => { e.stopPropagation(); goToSalonDetails(salon); }} />
                    ))}
                    {afternoonSlots.length === 0 && (
                      <Chip label="—" size="small" variant="outlined" disabled />
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            {/* Actions */}
            <Box sx={{ mt: isMobile ? 1 : 'auto', display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  goToSalonDetails(salon);
                }}
                sx={{ textTransform: 'none', backgroundColor: theme.colors.primary[500], '&:hover': { backgroundColor: theme.colors.primary[600] } }}
              >
                {t('actions.book')}
              </Button>
            </Box>
          </CardContent>
        </Box>
      </Card>
    )
  }

  // --- MAIN RETURN ---
  return (
    <Box sx={{
      minHeight: "100vh",
      height: hasActiveSearch && !isMobile ? "100vh" : (hasActiveSearch ? "auto" : "100vh"),
      display: "flex",
      flexDirection: "column",
      backgroundColor: hasActiveSearch ? theme.colors.secondary[300] : "#ffffff",
      overflowY: hasActiveSearch && !isMobile ? "hidden" : "auto",
      overflowX: "hidden",
      width: "100%",
      maxWidth: "100%",
      position: "relative"
    }}>
      {/* Hero Section with Background Image - Only shown when no search */}
      {!hasActiveSearch && (
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          backgroundImage: 'url(https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.70) 0%, rgba(232, 204, 170, 0.30) 55%, rgba(214, 142, 84, 0.55) 100%)',
            // backdropFilter: 'blur(8px)',
            // WebkitBackdropFilter: 'blur(8px)',
            zIndex: 1
          }
        }}>
          {/* Back button - Top Left */}
          <Box sx={{
            position: 'absolute',
            top: isMobile ? 16 : 24,
            left: isMobile ? 16 : 24,
            zIndex: 3
          }}>
            <Button
              variant="text"
              size="medium"
              onClick={() => navigate('/')}
              startIcon={<ArrowBackIcon />}
              sx={{
                color: 'white',
                textTransform: 'none',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
                px: 2,
                py: 1,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }
              }}
            >
              {t('actions.back')}
            </Button>
          </Box>

          <Box sx={{
            position: 'relative',
            zIndex: 2,
            textAlign: 'center',
            px: isMobile ? 2 : 4,
            maxWidth: '1200px',
            width: '100%'
          }}>
            {/* Title */}
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: isMobile ? '2rem' : '3.5rem',
                mb: 2,
                textShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}
            >
              {t('landing_search_salon.hero_title')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontSize: isMobile ? '1rem' : '1.25rem',
                mb: 6,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              {t('landing_search_salon.hero_subtitle')}
            </Typography>

            {/* Search Bar on Hero */}
            <Box sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'center',
              gap: isMobile ? 1.5 : 2,
              width: '100%'
            }}>
              <UnifiedSalonSearchForm
                categories={unifiedCategories}
                categoriesLoading={categoriesLoading}
                initialValue={searchFormValue}
                onSearch={handleUnifiedSearch}
                loading={isLoading}
              />
              {/* The former split location/name form is kept here temporarily
                  as commented reference while the unified search rolls out.
              <Paper
                component="form"
                onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '92%' : '100%',
                  maxWidth: isMobile ? '92%' : '1000px',
                  mx: isMobile ? 'auto' : 0,
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '12px' : '8px',
                  p: isMobile ? '16px' : '4px 8px',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '92%' : 'auto',
                  flex: isMobile ? 'none' : 1.7,
                  minWidth: isMobile ? 'auto' : '240px',
                  mx: isMobile ? 'auto' : 0,
                  backgroundColor: isMobile ? 'rgba(0,0,0,0.02)' : 'transparent',
                  borderRadius: isMobile ? '8px' : '0',
                  px: isMobile ? 1 : 0
                }}>
                  <LocationOnOutlinedIcon sx={{ p: '10px', color: 'text.secondary' }} />
                  <InputBase
                    inputRef={locationInputRef}
                    placeholder={t('landing_search_salon.placeholder_city_postal')}
                    value={searchParams.location}
                    onChange={handleLocationChange}
                    onKeyDown={handleKeyPress}
                    sx={{
                      ml: 1,
                      flex: 1,
                      width: '100%',
                      '& .MuiInputBase-input': {
                        height: isMobile ? '44px' : '48px',
                        padding: '0 16px',
                        fontSize: isMobile ? '16px' : '1rem',
                        '&::placeholder': {
                          opacity: 0.7
                        }
                      }
                    }}
                  />
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '92%' : 'auto',
                  flex: isMobile ? 'none' : 2,
                  minWidth: isMobile ? 'auto' : '250px',
                  mx: isMobile ? 'auto' : 0,
                  backgroundColor: isMobile ? 'rgba(0,0,0,0.02)' : 'transparent',
                  borderRadius: isMobile ? '8px' : '0',
                  px: isMobile ? 1 : 0
                }}>
                  <FormSelect
                    style={{ width: '100%' }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '& .MuiInputBase-input': {
                        padding: isMobile ? '10px 14px' : '12px 14px',
                        fontSize: isMobile ? '16px' : '1rem'
                      },
                      '& .MuiSelect-select': { textAlign: 'left' }
                    }}
                    name="category"
                    label={categoriesLoading ? t('common.loading') : t('landing_search_salon.all_categories')}
                    options={categoriesLoading ? [{ id: '', title: t('common.loading') }] : [{ id: '', title: t('landing_search_salon.all_categories') }, ...((categories || []).map((category) => ({ id: category?.category_id != null ? String(category.category_id) : '', title: getTranslatedField(category, 'name') ?? '' })))]}
                    value={String(searchParams.categoryId ?? '')}
                    onChange={handleCategoryChange}
                    disabled={categoriesLoading}
                    hideLabel
                  />
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '92%' : 'auto',
                  flex: isMobile ? 'none' : 2,
                  minWidth: isMobile ? 'auto' : '280px',
                  mx: isMobile ? 'auto' : 0,
                  backgroundColor: isMobile ? 'rgba(0,0,0,0.02)' : 'transparent',
                  borderRadius: isMobile ? '8px' : '0',
                  px: isMobile ? 1 : 0
                }}>
                  <SearchIcon sx={{ p: '10px', color: 'text.secondary' }} />
                  <InputBase
                    inputRef={salonNameInputRef}
                    placeholder={t('landing_search_salon.placeholder_search_by_name')}
                    value={salonNameSearch}
                    onChange={handleSalonNameChange}
                    onKeyDown={handleKeyPress}
                    sx={{
                      ml: 1,
                      flex: 1,
                      width: '100%',
                      '& .MuiInputBase-input': {
                        height: isMobile ? '44px' : '48px',
                        padding: '0 16px',
                        fontSize: isMobile ? '16px' : '1rem',
                        '&::placeholder': {
                          opacity: 0.7
                        }
                      }
                    }}
                  />
                </Box>
                <Box sx={{
                  position: 'absolute',
                  right: isMobile ? 12 : 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: isLoading ? 'flex' : 'none',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  color: theme.colors.primary[600],
                  pointerEvents: 'none'
                }}>
                  <CircularProgress size={20} />
                  <Typography variant="caption">{t('landing_search_salon.searching')}</Typography>
                </Box>
              </Paper>
              <Button
                type="button"
                onClick={handleSearch}
                disabled={isLoading}
                variant="contained"
                startIcon={<SearchIcon />}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  minHeight: isMobile ? 48 : 56,
                  width: isMobile ? '92%' : 'auto',
                  px: 3,
                  mx: isMobile ? 'auto' : 0,
                  backgroundColor: theme.colors.primary[600],
                  '&:hover': { backgroundColor: theme.colors.primary[700] }
                }}
              >
                {t('landing_search_salon.search')}
              </Button>
              */}
            </Box>
          </Box>
        </Box>
      )}

      {/* Header with search - Only shown when search is active */}
      {hasActiveSearch && (
        <Box sx={{
          position: isMobile ? 'sticky' : 'fixed',
          top: 0,
          left: isMobile ? 'auto' : 0,
          right: isMobile ? 'auto' : 0,
          height: activeSearchHeaderHeight,
          backgroundImage: 'url(https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderBottom: "1px solid #e0e0e0",
          zIndex: 100,
          pt: isMobile ? 1 : 1.5,
          px: isMobile ? 2 : 4,
          transition: 'all 0.3s ease',
          overflow: 'visible',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${theme.colors.primary[600]}CC 0%, ${theme.colors.secondary[400]}CC 100%)`,
            zIndex: 1
          }
        }}>
          {/* Back button */}
          <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'flex-start', mb: isMobile ? 2 : 1.5 }}>
            <Button
              variant="text"
              size="medium"
              onClick={() => navigate('/')}
              startIcon={<ArrowBackIcon />}
              sx={{
                color: 'white',
                textTransform: 'none',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
                px: 2,
                py: 1,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }
              }}
            >
              {t('actions.back')}
            </Button>
          </Box>

          {/* Titles */}
          <Box className="mb-6" sx={{ position: 'relative', zIndex: 2, textAlign: isMobile ? 'center' : 'left', width: '100%', mb: isMobile ? 2 : 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'inherit',
                lineHeight: 1.2,
                textAlign: 'left',
                width: '100%',
                color: 'white !important',
                fontWeight: 700,
                fontSize: isMobile ? (isSmallMobile ? "2rem" : "2.5rem") : "2.4rem",
                textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                mb: 0.75
              }}
            >
              {t('landing_search_salon.hero_title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'white !important',
                mt: 1,
                textAlign: 'left',
                width: '100%',
                fontSize: isMobile ? (isSmallMobile ? "1rem" : "1.1rem") : "1rem",
                textShadow: '0 2px 8px rgba(0,0,0,0.5)'
              }}
            >
              {t('landing_search_salon.hero_subtitle')}
            </Typography>
          </Box>

          {/* --- UNIFIED SEARCH BAR --- */}
          {/* Adjusted padding/margin for search bar to align with new layout */}
          <Box sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'center',
            gap: isMobile ? 1.5 : 2,
            width: '100%',
            pb: isMobile ? 2 : 2,
            pt: isMobile ? 2 : 1,
          }}>
            <UnifiedSalonSearchForm
              categories={unifiedCategories}
              categoriesLoading={categoriesLoading}
              initialValue={searchFormValue}
              onSearch={handleUnifiedSearch}
              loading={isLoading}
            />
            {/* The former split location/name form is intentionally not rendered.
            <Paper
              component="form"
              onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: isMobile ? '92%' : '100%',
                maxWidth: isMobile ? '92%' : '1000px',
                minWidth: isMobile ? '92%' : 0,
                mx: isMobile ? 'auto' : 0,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '8px',
                p: isMobile ? '16px' : '4px 8px',
                pr: isMobile ? 2 : 2,
                mr: isMobile ? '0' : '12px',
                textAlign: 'left',
                position: 'relative',
                backgroundColor: 'white'
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: isMobile ? '92%' : 'auto',
                flex: isMobile ? 'none' : 1.7,
                minWidth: isMobile ? 'auto' : '240px',
                mx: isMobile ? 'auto' : 0,
                backgroundColor: isMobile ? 'rgba(0,0,0,0.02)' : 'transparent',
                borderRadius: isMobile ? '8px' : '0',
                px: isMobile ? 1 : 0
              }}>
                <LocationOnOutlinedIcon sx={{ p: '10px', color: 'text.secondary' }} />
                <InputBase
                  inputRef={locationInputRef}
                  placeholder={t('landing_search_salon.placeholder_city_postal')}
                  value={searchParams.location}
                  onChange={handleLocationChange}
                  onKeyDown={handleKeyPress}
                  sx={{
                    ml: 1,
                    flex: 1,
                    width: '100%',
                    '& .MuiInputBase-input': {
                      height: isMobile ? '44px' : '48px',
                      padding: '0 16px',
                      fontSize: isMobile ? '16px' : '1rem',
                      '&::placeholder': {
                        opacity: 0.7
                      }
                    },
                    '&:before, &:after': {
                      display: 'none'
                    }
                  }}
                  className="bg-input border-border focus:ring-primary"
                />
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: isMobile ? '92%' : 'auto',
                flex: isMobile ? 'none' : 2,
                minWidth: isMobile ? 'auto' : '250px',
                mx: isMobile ? 'auto' : 0,
                backgroundColor: isMobile ? 'rgba(0,0,0,0.02)' : 'transparent',
                borderRadius: isMobile ? '8px' : '0',
                px: isMobile ? 1 : 0
              }}>
                <FormSelect
                  style={{ width: '100%' }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiInputBase-input': {
                      padding: isMobile ? '10px 14px' : '12px 14px',
                      fontSize: isMobile ? '0.9rem' : '1rem'
                    },
                    '& .MuiSelect-select': { textAlign: 'left' }
                  }}
                  name="category"
                  label={categoriesLoading ? t('common.loading') : t('landing_search_salon.all_categories')}
                  options={categoriesLoading ? [{ id: '', title: t('common.loading') }] : [{ id: '', title: t('landing_search_salon.all_categories') }, ...((categories || []).map((category) => ({ id: category?.category_id != null ? String(category.category_id) : '', title: getTranslatedField(category, 'name') ?? '' })))]}
                  value={String(searchParams.categoryId ?? '')}
                  onChange={handleCategoryChange}
                  disabled={categoriesLoading}
                  hideLabel
                />
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: isMobile ? '92%' : 'auto',
                flex: isMobile ? 'none' : 2,
                minWidth: isMobile ? 'auto' : '280px',
                mx: isMobile ? 'auto' : 0,
                backgroundColor: isMobile ? 'rgba(0,0,0,0.02)' : 'transparent',
                borderRadius: isMobile ? '8px' : '0',
                px: isMobile ? 1 : 0
              }}>
                <SearchIcon sx={{ p: '10px', color: 'text.secondary' }} />
                <InputBase
                  inputRef={salonNameInputRef}
                  placeholder={t('landing_search_salon.placeholder_search_by_name')}
                  value={salonNameSearch}
                  onChange={handleSalonNameChange}
                  onKeyDown={handleKeyPress}
                  sx={{
                    ml: 1,
                    flex: 1,
                    width: '100%',
                    '& .MuiInputBase-input': {
                      height: isMobile ? '44px' : '48px',
                      padding: '0 16px',
                      fontSize: isMobile ? '16px' : '1rem',
                      '&::placeholder': {
                        opacity: 0.7
                      }
                    },
                    '&:before, &:after': {
                      display: 'none'
                    }
                  }}
                  className="bg-input border-border focus:ring-primary"
                />
              </Box>
              <Box sx={{
                position: 'absolute',
                right: isMobile ? 12 : 16,
                top: '50%',
                transform: 'translateY(-50%)',
                display: isLoading ? 'flex' : 'none',
                alignItems: 'center',
                gap: 1,
                px: 1,
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                py: 1,
                pointerEvents: 'none'
              }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                <Typography variant="caption" sx={{ color: 'white' }}>{t('landing_search_salon.searching')}</Typography>
              </Box>
            </Paper>
            <Button
              type="button"
              onClick={handleSearch}
              disabled={isLoading}
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                minHeight: isMobile ? 48 : 56,
                width: isMobile ? '92%' : 'auto',
                px: 3,
                mx: isMobile ? 'auto' : 0,
                backgroundColor: theme.colors.primary[600],
                '&:hover': { backgroundColor: theme.colors.primary[700] }
              }}
            >
              {t('landing_search_salon.search')}
            </Button>
            */}
            {isMobile && (
              <Button
                variant="outlined"
                size="small"
                startIcon={showMapOnMobile ? <ListIcon /> : <MapIcon />}
                onClick={() => setShowMapOnMobile(!showMapOnMobile)}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  px: 2,
                  py: 0.75,
                  mt: 1,
                  borderColor: theme.colors.primary[500],
                  color: 'white',
                  backgroundColor: theme.colors.primary[600],
                  boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                  width: '92%',
                  mx: 'auto',
                  '&:hover': {
                    backgroundColor: theme.colors.primary[700],
                    borderColor: theme.colors.primary[700]
                  }
                }}
              >
                {showMapOnMobile ? t('landing_search_salon.see_list') : t('landing_search_salon.see_map')}
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Main content area */}
      <Box sx={{
        flex: 1,
        display: hasActiveSearch ? "flex" : "none",
        backgroundColor: theme.colors.secondary[80],
        flexDirection: isMobile ? "column" : "row",
        height: activeSearchPanelHeight,
        minHeight: isMobile ? "100vh" : 0,
        position: "relative",
        mt: isMobile ? 0 : `${activeSearchHeaderHeight}px`,
        pt: 0,
        overflow: isMobile ? "visible" : "hidden"
      }}>
        {/* Map - Left Side */}
        <Box sx={{
          width: isMobile ? "100%" : "40%",
          height: isMobile ? (showMapOnMobile ? "100vh" : "300px") : "100%",
          display: isMobile ? (showMapOnMobile ? "block" : "none") : "block",
          order: isMobile ? 2 : 1,
          pl: 0,
          position: isMobile ? (showMapOnMobile ? "fixed" : "relative") : "relative",
          top: isMobile ? (showMapOnMobile ? 0 : undefined) : undefined,
          left: isMobile ? (showMapOnMobile ? 0 : undefined) : 0,
          right: isMobile ? (showMapOnMobile ? 0 : undefined) : 0,
          bottom: isMobile ? (showMapOnMobile ? 0 : undefined) : undefined,
          alignSelf: "flex-start",
          zIndex: 1
        }}>
          {isMobile && showMapOnMobile && (
            <Button
              variant="contained"
              size="small"
              startIcon={<ListIcon />}
              onClick={() => setShowMapOnMobile(false)}
              sx={{
                position: 'fixed',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1200,
                borderRadius: '20px',
                textTransform: 'none',
                fontSize: '0.85rem',
                px: 2,
                py: 0.75,
                backgroundColor: theme.colors.primary[600],
                '&:hover': {
                  backgroundColor: theme.colors.primary[700]
                }
              }}
            >
              {t('landing_search_salon.see_list')}
            </Button>
          )}
          <Paper elevation={0} sx={{ height: "100%", width: "100%", borderRadius: 0, position: "relative" }}>
            <Box ref={mapRef} sx={{ width: "100%", height: "100%", position: "relative", zIndex: 1 }} />
          </Paper>
        </Box>
        {/* Search Results - Right Side */}
        <Box sx={{
          width: isMobile ? "100%" : "60%",
          height: isMobile ? "auto" : "100%",
          minHeight: isMobile ? "50vh" : 0,
          backgroundColor: theme.colors.secondary[80],
          borderLeft: isMobile ? 'none' : "1px solid #e0e0e0",
          borderTop: isMobile ? "1px solid #e0e0e0" : 'none',
          display: "flex",
          flexDirection: "column",
          order: isMobile ? 1 : 2,
          ml: 0,
          pr: isMobile ? 0 : 2,
          overflow: "hidden"
        }}>
          <Box sx={{ p: isMobile ? 1.5 : 2, borderBottom: "1px solid #e0e0e0", flexShrink: 0, display: isMobile && showMapOnMobile ? 'none' : 'block' }}>
            {/* Mobile Map Toggle */}
            {isMobile && false && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={showMapOnMobile ? <ListIcon /> : <MapIcon />}
                  onClick={() => setShowMapOnMobile(!showMapOnMobile)}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    px: 2,
                    py: 0.5,
                    borderColor: theme.colors.primary[500],
                    color: theme.colors.primary[600],
                    '&:hover': {
                      backgroundColor: theme.colors.primary[50],
                      borderColor: theme.colors.primary[600]
                    }
                  }}
                >
                  {showMapOnMobile ? t('landing_search_salon.see_list') : t('landing_search_salon.see_map')}
                </Button>
              </Box>
            )}
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: isMobile ? '1.1rem' : '1.25rem' }}>{t('landing_search_salon.results_title')}</Typography>
            {totalResults > 0 && (<Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.85rem' : '0.875rem' }}>{t('landing_search_salon.results_count', { count: totalResults })}</Typography>)}
          </Box>
          <Box
            ref={resultsScrollRef}
            onScroll={handleResultsScroll}
            sx={{
            flex: 1,
            minHeight: 0,
            p: isMobile ? 1.5 : 2,
            overflowY: isMobile ? "visible" : "auto",
            overscrollBehavior: "contain",
            display: isMobile ? (showMapOnMobile ? "none" : "block") : "block"
          }}>
            {isLoading ? (
              <Box sx={{ textAlign: "center", mt: isMobile ? 2 : 4 }}>
                <CircularProgress size={isMobile ? 32 : 40} />
                <Typography sx={{ mt: 2, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                  {lastSearchTerm
                    ? t('landing_search_salon.search_in_progress_for', { term: lastSearchTerm })
                    : t('landing_search_salon.search_in_progress')}
                </Typography>
              </Box>
            ) : showNoResultsMessage ? (
              <Typography sx={{ textAlign: "center", mt: isMobile ? 2 : 4, color: "text.secondary", fontSize: isMobile ? '0.9rem' : '1rem', px: isMobile ? 1 : 0 }}>
                {salonNameSearch
                  ? t('landing_search_salon.no_results_for', { term: salonNameSearch })
                  : t('landing_search_salon.no_results_criteria')}
              </Typography>
            ) : (
              <>
                {paginatedSalons.map((salon) => (
                  <SalonCard key={salon.salon_id || salon.id} salon={salon} />
                ))}
                {pageCount > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Pagination
                      count={pageCount}
                      page={currentPage}
                      onChange={(event, page) => {
                        const nextRequest = {
                          ...requestParams,
                          page,
                        };
                        setCurrentPage(page);
                        setRequestParams(nextRequest);
                        navigate(buildSearchSalonUrl({
                          ...searchFormValue,
                          page,
                          active: true,
                        }), { replace: true });
                      }}
                      color="primary"
                      size={isMobile ? "small" : "medium"}
                      siblingCount={isMobile ? 0 : 1}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default SearchSalonPage;





